import { useQuery, useMutation } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

import { convertObjectToCamelCase } from '@/helpers'
import {
  IAReactivationMetaTemplateProps,
  IAReactivationMetaTemplateCreateResponseProps,
  IAReactivationMetaTemplateCreateRequestProps,
} from '@/types'

import { QUERIES_ADMIN } from './api.constants'
import { api } from './api.service'
import {
  AI_REACTIVATION_META_TEMPLATE_LIST_URL,
  AI_REACTIVATION_URL,
} from './api.urls'

export const useAiReactiovationMetaTemplateList = (
  params?: Record<string, string | number>,
) => {
  const getKaiMetaTemplateList = async (): Promise<{
    success: boolean
    message: string
    data?: IAReactivationMetaTemplateProps[]
  }> => {
    try {
      const { data } = await api.get(AI_REACTIVATION_META_TEMPLATE_LIST_URL, {
        params,
      })

      return {
        success: true,
        message: 'Reactivation meta template fetched successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as IAReactivationMetaTemplateProps[],
      }
    } catch (err) {
      const message = 'Failed to get reactivation meta template!'

      if (isAxiosError(err) && err.response && err.response.data) {
        return {
          success: false,
          message,
          data: err.response.data,
        }
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  const useQueryReturn = useQuery({
    queryKey: [QUERIES_ADMIN.AI_REACTIVATION_META_TEMPLATE.module, params],
    queryFn: () => getKaiMetaTemplateList(),
  })

  return useQueryReturn
}

export const useAiReactiovationMetaTemplateCreate = () => {
  const getKaiMetaTemplateCreate = async (
    payload: IAReactivationMetaTemplateCreateRequestProps,
  ): Promise<{
    success: boolean
    message: string
    data?: IAReactivationMetaTemplateCreateResponseProps
  }> => {
    try {
      const { data } = await api.post(AI_REACTIVATION_URL, payload)

      return {
        success: true,
        message: 'Reactivation created successfully!',
        data: convertObjectToCamelCase(
          data,
        ) as IAReactivationMetaTemplateCreateResponseProps,
      }
    } catch (err) {
      const message = 'Failed to create reactivation!'

      if (isAxiosError(err) && err.response && err.response.data) {
        return {
          success: false,
          message,
          data: err.response.data,
        }
      }

      return {
        success: false,
        message,
        data: undefined,
      }
    }
  }

  const useMutationReturn = useMutation({
    mutationKey: [QUERIES_ADMIN.AI_REACTIVATION_META_TEMPLATE.module],
    mutationFn: (payload: IAReactivationMetaTemplateCreateRequestProps) =>
      getKaiMetaTemplateCreate(payload),
  })

  return useMutationReturn
}
